import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn, LayoutDashboard, LogOut, Menu, X } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import { withUtm } from "@/lib/utm";

const NAV_LINKS = [
  { to: "/", label: "الرئيسية", testid: "nav-link-home" },
  { to: "/opportunities", label: "فرص التدريب التعاوني", testid: "nav-link-opportunities" },
  { to: "/#about", label: "عن عماد", testid: "nav-link-about", isHash: true },
  { to: "/partners", label: "شركاء النجاح", testid: "nav-link-partners" },
  { to: "/faq", label: "الأسئلة الشائعة", testid: "nav-link-faq" },
];

export default function PublicNav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isHome = location.pathname === "/";
  const closeBtnRef = useRef(null);
  const openerRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.hash]);

  // Escape closes + focus close button on open + return focus to opener + scroll lock + restore scrollY
  useEffect(() => {
    if (!mobileOpen) return;
    if (typeof document === "undefined") return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    document.addEventListener("keydown", onKey);
    setTimeout(() => closeBtnRef.current?.focus(), 30);
    // iOS-safe body scroll lock preserving scrollY
    const y = window.scrollY || window.pageYOffset || 0;
    const prevOverflow = document.body.style.overflow;
    const prevPosition = document.body.style.position;
    const prevTop = document.body.style.top;
    const prevWidth = document.body.style.width;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${y}px`;
    document.body.style.insetInlineStart = "0";
    document.body.style.insetInlineEnd = "0";
    document.body.style.width = "100%";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      document.body.style.position = prevPosition;
      document.body.style.top = prevTop;
      document.body.style.insetInlineStart = "";
      document.body.style.insetInlineEnd = "";
      document.body.style.width = prevWidth;
      window.scrollTo(0, y);
      openerRef.current?.focus();
    };
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    toast.success("تم تسجيل الخروج");
    navigate("/");
  };

  const transparent = isHome && !scrolled;
  const barCls = transparent
    ? "bg-gradient-to-b from-slate-950/55 via-slate-950/25 to-transparent"
    : "bg-[url('https://customer-assets-39nsmqrw.emergentagent.net/job_talent-hub-822/artifacts/6kgk4q6z_emad_slider_workshop.webp')] bg-cover bg-center border-b border-slate-900/40 shadow-md before:absolute before:inset-0 before:bg-slate-950/60 before:pointer-events-none";
  const linkCls = transparent
    ? "text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.35)]"
    : "text-white [text-shadow:0_1px_3px_rgba(0,0,0,0.5)] hover:text-teal-200";
  const textColor = transparent ? "text-white" : "text-white";
  const subColor = transparent ? "text-white/80" : "text-white/80";
  const logoInvert = "";

  const renderNavLink = (l) =>
    l.isHash ? (
      <a key={l.to} href={l.to} className="hover:text-teal-500 transition-colors" data-testid={l.testid}>
        {l.label}
      </a>
    ) : (
      <Link key={l.to} to={withUtm(l.to)} className="hover:text-teal-500 transition-colors" data-testid={l.testid}>
        {l.label}
      </Link>
    );

  return (
    <header className={`sticky top-0 z-30 relative ${barCls}`} data-testid="public-nav">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 md:h-28 flex items-center justify-between relative z-10">
        <Link to={withUtm("/")} className={`flex items-center gap-3 ${logoInvert}`} data-testid="nav-logo">
          <Logo variant={transparent ? "navy" : "transparent"} size={120} textColor={textColor} subColor={subColor} />
        </Link>

        <nav className={`hidden lg:flex items-center gap-6 xl:gap-8 text-sm font-medium ${linkCls}`}>
          {NAV_LINKS.map(renderNavLink)}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <ThemeToggle className={transparent ? "text-white hover:bg-white/10" : ""} />
          <Link
            to={withUtm("/partners")}
            className={`hidden lg:inline-flex whitespace-nowrap items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              transparent ? "text-white bg-white/10 hover:bg-white/20 border border-white/25" : "text-teal-800 bg-amber-50 hover:bg-amber-100 border border-amber-200"
            }`}
            data-testid="partner-cta-btn"
          >
            انضم لشركاء عماد
          </Link>
          {user && user.role ? (
            <>
              <Link
                to="/dashboard"
                className="hidden sm:inline-flex whitespace-nowrap items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-medium hover:bg-teal-600 transition-colors"
                data-testid="go-dashboard-btn"
              >
                <LayoutDashboard className="h-4 w-4" />
                لوحة التحكم
              </Link>
              <button
                onClick={handleLogout}
                className={`hidden lg:inline-flex p-2 rounded-lg ${transparent ? "text-white/90 hover:bg-white/10" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid="nav-logout-btn"
                title="تسجيل الخروج"
                aria-label="تسجيل الخروج"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </>
          ) : (
            <>
              <Link
                to={withUtm("/login")}
                className={`hidden sm:inline-flex whitespace-nowrap items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  transparent ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"
                }`}
                data-testid="login-nav-btn"
              >
                <LogIn className="h-4 w-4" />
                دخول
              </Link>
              <Link
                to={withUtm("/register")}
                className="hidden sm:inline-flex whitespace-nowrap items-center gap-2 px-5 py-2 rounded-lg bg-teal-700 text-white text-sm font-medium hover:bg-teal-600 hover:-translate-y-0.5 transition-all shadow-sm"
                data-testid="register-nav-btn"
              >
                إنشاء حساب
              </Link>
            </>
          )}

          {/* Mobile/Tablet menu opener (up to lg) */}
          <button
            ref={openerRef}
            type="button"
            onClick={() => setMobileOpen(true)}
            className={`lg:hidden inline-flex items-center justify-center min-h-[44px] min-w-[44px] rounded-lg ${
              transparent ? "text-white hover:bg-white/10 border border-white/25" : "text-slate-700 hover:bg-slate-100 border border-slate-200"
            }`}
            aria-label="فتح القائمة"
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav-panel"
            data-testid="mobile-menu-btn"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Mobile/Tablet menu overlay — rendered via Portal to document.body so it is NEVER
          a descendant of the sticky header (which has backdrop-blur). Required for
          iPhone Safari where backdrop-filter/transform ancestors clip fixed descendants. */}
      {mobileOpen && typeof document !== "undefined" && createPortal(
        <div
          className="fixed inset-0 lg:hidden"
          style={{ zIndex: 100, minHeight: "100vh", height: "100vh" }}
          role="dialog"
          aria-modal="true"
          aria-label="القائمة الرئيسية"
          data-testid="mobile-nav-panel"
          id="mobile-nav-panel"
        >
          <button
            type="button"
            aria-label="إغلاق القائمة"
            onClick={() => setMobileOpen(false)}
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-sm"
            tabIndex={-1}
          />
          <div
            className="absolute bg-white shadow-2xl flex flex-col overflow-y-auto animate-in slide-in-from-right"
            style={{
              insetBlockStart: 0,
              insetBlockEnd: 0,
              insetInlineEnd: 0,
              width: "86%",
              maxWidth: "24rem",
              overscrollBehavior: "contain",
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <Logo variant="navy" size={96} textColor="text-slate-900" subColor="text-slate-500" />
              <button
                ref={closeBtnRef}
                type="button"
                onClick={() => setMobileOpen(false)}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                aria-label="إغلاق القائمة"
                data-testid="mobile-menu-close-btn"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="p-4 flex flex-col gap-1" aria-label="روابط الصفحة">
              {NAV_LINKS.map((l) =>
                l.isHash ? (
                  <a
                    key={l.to}
                    href={l.to}
                    className="px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-cream-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </a>
                ) : (
                  <Link
                    key={l.to}
                    to={withUtm(l.to)}
                    className="px-4 py-3 rounded-xl text-slate-800 font-semibold hover:bg-cream-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-${l.testid}`}
                  >
                    {l.label}
                  </Link>
                )
              )}
            </nav>

            <div className="mt-auto p-5 border-t border-cream-200 space-y-3">
              <Link
                to={withUtm("/partners")}
                className="block w-full text-center px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 text-teal-800 font-bold hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50"
                data-testid="mobile-partner-cta"
              >
                انضم لشركاء عماد
              </Link>
              {user && user.role ? (
                <>
                  <Link
                    to="/dashboard"
                    className="block w-full text-center px-4 py-3 rounded-lg bg-teal-700 text-white font-bold hover:bg-teal-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    data-testid="mobile-dashboard-btn"
                  >
                    لوحة التحكم
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-center px-4 py-3 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50"
                    data-testid="mobile-logout-btn"
                  >
                    تسجيل الخروج
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to={withUtm("/login")}
                    className="block w-full text-center px-4 py-3 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    data-testid="mobile-login-btn"
                  >
                    تسجيل الدخول
                  </Link>
                  <Link
                    to={withUtm("/register")}
                    className="block w-full text-center px-4 py-3 rounded-lg bg-teal-700 text-white font-bold hover:bg-teal-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500/50"
                    data-testid="mobile-register-btn"
                  >
                    إنشاء حساب
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </header>
  );
}
